<template>
  <b-row class="px-3 justify-content-between">
    <b-col cols="12">
      <div class="d-flex flex-wrap flex-lg-nowrap filter-gap">
        <div v-if="usePickerFilter" class="filter-width">
          <filter-site-picker :use-default-value="true" :multiple="multiple" :select-all="multiple" operator="equals"
            :item-key="siteIdFilterProperty" :item-label="siteLabelFilterProperty"
            :item-currency="siteCurrencyFilterProperty" :group-by-key="groupKey" :group-by-label="groupLabel"
            @filterWithIdChanged="siteFilterChanged($event)" />
        </div>

        <div class="d-flex filter-gap filter-width flex-wrap">
          <date-range-picker :allow-future-date="allowFutureDate" :date-picker-format="datePickerFormat"
            :date-range-to-force="dateRangeToForce" :use-stored-date-range="useStoredDateRange"
            :date-dimension="dateDimension" :time-range-value="timeRangeValue" :use-time-filter="useTimeFilter"
            :use-month-only="useMonthOnly" :use-month-label="useMonthLabel"
            @dateRangeFilterUpdated="dateRangeFilterUpdated($event)"
            @labelPeriodHoursUpdated="labelPeriodHoursUpdated($event)" />

          <time-range-picker :class="{ hidden: !useTimeFilter }" :use-default-value="true"
            @timeRangeFilterChanged="timeRangeFilterUpdated($event)" />
        </div>

        <b-dropdown toggle-class="dropdown-style" right :text="$t('export.export')"
          class="ml-lg-auto align-self-center align-self-lg-start">
          <b-dropdown-item class="mb-2" :disabled="!dataReady" @click="downloadPdf()">
            <font-awesome-icon icon="file-pdf" class="mr-2 export-icon-pdf" />
            {{ $t("export.PDF") }}
          </b-dropdown-item>
          <b-dropdown-item class="mb-2" :disabled="!dataReady" @click="downloadExcel()">
            <font-awesome-icon icon="file-excel" class="mr-2 export-icon-excel" />
            {{ $t("export.excel") }}
          </b-dropdown-item>
          <b-dropdown-item :disabled="!dataReady" @click="downloadCsv()">
            <font-awesome-icon icon="file-csv" class="mr-2 export-icon" />
            {{ $t("export.csv") }}
          </b-dropdown-item>
        </b-dropdown>
      </div>
    </b-col>
  </b-row>
</template>

<script>
import FilterSitePicker from "../picker/FilterSitePicker.vue";
import DateRangePicker from "@/components/picker/DateRangePicker.vue";
import TimeRangePicker from "@/components/picker/TimeRangePicker.vue";
import dimensionEnum from "@/model/dimensionEnum";
import { dateFormatEnum, dateFormat } from "@/filter/dateFormat";
import Tools from "@/utils/tools";

export default {
  name: "CommonActions",
  components: {
    FilterSitePicker,
    DateRangePicker,
    TimeRangePicker,
  },
  props: {
    useTimeFilter: {
      type: Boolean,
      default() {
        return true;
      },
    },
    usePickerFilter: {
      type: Boolean,
      default() {
        return true;
      },
    },
    allowFutureDate: {
      type: Boolean,
      default() {
        return false;
      },
    },
    /**  Dimension sur laquelle faire porter le filtre, à override dans le cas où on a plusieurs dates sur lesquels on veut filtrer pour une même entité*/
    dateDimension: {
      type: String,
      default() {
        return dimensionEnum.DATE;
      },
    },
    dataReady: {
      type: Boolean,
      default() {
        return false;
      },
    },
    multiple: {
      type: Boolean,
      default() {
        return true;
      },
    },
    /** Format d'affichage dans le date picker */
    datePickerFormat: {
      type: String,
      default() {
        return dateFormatEnum.DATE_SHORT_FORMAT;
      },
    },
    /** Pour indiquer des dates à imposer au picker à tout moment : [ debut, fin ], dates en string au format datePickerFormat.
     * Elles seront plus prioritaires que les dates stockées depuis une autre page,
     * et les remplaceront même dans le storage si useStoredDateRange = true
     */
    dateRangeToForce: {
      type: Array,
      default() {
        return [];
      },
    },
    /**
     * Indique s'il faut utiliser les dates stockées depuis une autre page au lieu de la date du jour par défaut.
     * Elles seront moins prioritaires que dateRangeToForce.
     */
    useStoredDateRange: {
      type: Boolean,
      default() {
        return true;
      },
    },
    /**
     * Libellé à utiliser comme première partie du nom de fichier pour les exports
     */
    filenamePrefix: {
      type: String,
      default() {
        return this.$t("commonFilters.fileNamePrefix");
      },
    },
    useMonthOnly: {
      type: Boolean,
      default() {
        return false;
      },
    },
    useMonthLabel: {
      type: Boolean,
      default() {
        return false;
      },
    },
  },
  data() {
    return {
      siteIdFilterProperty: dimensionEnum.SITE,
      siteLabelFilterProperty: dimensionEnum.SITE_NAME,
      siteCurrencyFilterProperty: dimensionEnum.SITE_CURRENCY,
      groupKey: dimensionEnum.SITE_SITE_CATEGORY,
      groupLabel: dimensionEnum.SITE_SITE_CATEGORY_NAME,
      /** Libellé des dates sélectionnées, telles qu'affichées dans le DateRangePicker */
      dateRangeStr: "",
      /** Libellé de la période (dates) sélectionnée */
      labelDatePeriod: "",
      /** Tableau d'objets des sites sélectionnés, indiquant leur id et leur label */
      siteValues: [],
      /** Filtre venant du FilterPicker, tel qu'attendu par cubeJs.
       * Ex :
       *    {
       *      dimension: "Site.label",
       *      operator: "equals",
       *      values: ["Site 1"]
       *    }
       */
      siteFilter: null,
      /** Filtre venant du DateRangePicker, tel qu'attendu par cubeJs.
       * Ex :
       *    {
       *      dimension: "BusinessDate.id",
       *      operator: "inDateRange",
       *      values: ["2020-09-23", "2020-09-26"]
       *    }
       */
      dateRangeFilter: null,
      /** Filtre venant du TimeRangePicker, tel qu'attendu par cubeJs.
       * NB : Il faut lister les valeurs possibles au lieu d'utiliser des filtres >,< car cubeJs ne supporte pas encore bien le OR (pour les horaires > 23)
       * Ex :
       *    {
       *      dimension: dimensionEnum.TIME_HOUR,
       *      operator: "equals",
       *      values: ["18", "19", "20", "21", "22", "23", "0", "1"]
       *    }
       */
      timeRangeFilter: null,
      isTimeFullRange: false,
      timeRangeValue: null,
    };
  },
  computed: {
    isDefaultQueryReady() {
      return (
        (this.usePickerFilter ? this.siteFilter : true) &&
        this.dateRangeFilter &&
        (this.timeRangeFilter || this.isTimeFullRange)
      );
    },
  },
  methods: {
    siteFilterChanged(value) {
      this.siteValues = value.values;
      this.siteFilter = value.idFilter;
      this.filterUpdated();
    },
    dateRangeFilterUpdated(value) {
      this.dateRangeFilter = value.dateRangeFilter || null;
      this.dateRangeStr = value.dateRangeStr || "";
      this.labelDatePeriod = value.labelPeriod || "";
      this.filterUpdated();
    },
    labelPeriodHoursUpdated(value) {
      this.labelDatePeriod = value.labelPeriod || "";
    },
    timeRangeFilterUpdated(value) {
      this.timeRangeValue = value || null;
      this.timeRangeFilter = value.timeRangeFilter || null;
      this.isTimeFullRange = value.isFullRange || false;
      this.filterUpdated();
    },
    optimizeTimeRange() {
      // Si la plage maximale est sélectionné, pas de filtre (optimisation des perfs)
      let optimizedTimeRangeFilter = this.timeRangeFilter;
      if (this.isTimeFullRange && this.dateRangeFilter && this.dateRangeFilter.values) {
        // Si les dates associées selectionnées intersectent avec la date d'aujourd'hui
        // on n'optimise pas la query au risque de consulter les tables de preagreggation
        // et donc d'avoir des tables pas tout à fait à jour au moment de la requête
        // on verifie d'abord que le filtrage par date est bien appliqué uniquement
        // sur un seul jour
        const beg = this.dateRangeFilter.values[0];
        const end = this.dateRangeFilter.values[1];
        if (beg === end) {
          // enfin on verifie que ce jour est aujourd'hui
          const today = Tools.BusinessDateHelper.getTodaysBusinessDate();
          const todayDate = dateFormat(today, "dateFilter");
          if (todayDate === beg) {
            // le filtrage contient de valeurs actuelles, on bypass les preaggregations
            optimizedTimeRangeFilter = {
              dimension: dimensionEnum.TIME_HOUR,
              operator: "equals",
              values: [...Array(24).keys()].map(k => String(k)),
            };
          }
        }
      }
      return optimizedTimeRangeFilter;
    },
    filterUpdated() {
      if (this.isDefaultQueryReady) {
        let filters = [];

        if (this.usePickerFilter && this.siteFilter) {
          filters.push(this.siteFilter);
        }

        const timeRange = this.optimizeTimeRange();
        if (timeRange) {
          filters.push(timeRange);
        }

        // Construire le timeDimension CubeJS par concaténation des dates et des heures sélectionnées
        const timeDimension = this.dateRangeFilter;

        this.$emit("commonFiltersUpdated", {
          filters,
          timeDimension,
          siteValues: this.siteValues,
        });
      }
    },
    /**
     * Construit le dateRange du timeDimension en concaténant les dates et les heures sélectionnées.
     * @param {string[]} dateRange - Ex : ["2026-03-01", "2026-03-31"]
     * @param {number|null} startHour - Heure de début (0-23)
     * @param {number|null} endHour - Heure de fin (0-23)
     * @param {boolean} isFullRange - Plage horaire complète
     * @returns {string[]} - Ex : ["2026-03-01T10:00:00", "2026-03-31T22:00:00"]
     */
    buildDateRangeWithTime(dateRange, startHour, endHour, isFullRange) {
      if (!dateRange || dateRange.length < 2) return dateRange;
      const pad = (n) => String(n).padStart(2, "0");
      if (isFullRange || startHour == null || endHour == null) {
        return [
          `${dateRange[0]}T00:00:00`,
          `${dateRange[1]}T23:59:59`,
        ];
      }
      return [
        `${dateRange[0]}T${pad(startHour)}:00:00`,
        `${dateRange[1]}T${pad(endHour)}:00:00`,
      ];
    },
    downloadPdf() {
      this.$emit("clickDownloadPdf", this.assembleDownloadData());
    },
    downloadExcel() {
      this.$emit("clickDownloadExcel", this.assembleDownloadData());
    },
    downloadCsv() {
      this.$emit("clickDownloadCsv", this.assembleDownloadData());
    },
    assembleDownloadData() {
      return {
        filename: Tools.StringHelper.constructExportFilename(
          this.filenamePrefix,
          this.multiple ? null : this.siteValues[0].label,
          this.dateRangeStr
        ),
        subtitles: Tools.StringHelper.constructExportSubtitles(
          this.siteValues.map((v) => v.label),
          this.labelDatePeriod,
          ""
        ),
      };
    },
  },
};
</script>

<style scoped>
.hidden {
  position: absolute;
  width: 1px;
  height: 1px;
  margin: -1px;
  border: 0;
  padding: 0;
  clip: rect(0 0 0 0);
  overflow: hidden;
}
</style>
